import React, {useEffect, useRef, useState} from "react";
import {createUseStyles} from "react-jss";
import Hls from "hls.js";
import {Play, Pause} from "lucide-react";
import {getBackgroundImageString} from "../helpers/helpers";

const useStyles = createUseStyles({
    video: {
        objectFit: "cover",
        width: "100%",
        height: "100%",
        position: "absolute",
        top: 0,
        left: 0,
        maskImage: "-webkit-radial-gradient(white, black)",
        backfaceVisibility: "hidden",
    },
    videoGradient: {
        backgroundImage: ({ gradient }) =>
            getBackgroundImageString("", gradient),
        width: "100%",
        height: "100%",
        position: "absolute",
        zIndex: 1,
        top: 0,
        left: 0,
    },
    bgVideoToggle: {
        position: "absolute",
        bottom: 16,
        right: 16,
        zIndex: 5,
        backgroundColor: "rgba(0, 0, 0, 0.6)",
        color: "#FFFFFF",
        border: "none",
        borderRadius: "50%",
        width: 44,
        height: 44,
        minWidth: 44,
        minHeight: 44,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        outline: "none",
        transition: "background-color 0.2s, transform 0.1s",
        "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.85)",
            transform: "scale(1.05)",
        },
        "&:focus-visible": {
            outline: "2px solid #4484F1",
            outlineOffset: "2px",
        },
    },
});

export const BackgroundVideo = ({
    videoUrl,
    active = true,
    videoPausedByDefault = false,
    gradient,
    className = ""
}) => {
    const classes = useStyles({ gradient });
    const videoRef = useRef(null);
    const playPromiseRef = useRef(null);
    const hlsRef = useRef(null);

    const [isPaused, setIsPaused] = useState(() => {
        if (videoPausedByDefault) return true;
        if (typeof window !== "undefined" && window.matchMedia) {
            return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        }
        return false;
    });

    useEffect(() => {
        const video = videoRef.current;
        if (!video || !videoUrl) return;

        const playlist = `https://ems.dimu.org/streaming/adaptive/${videoUrl}/index.m3u8`;

        if (Hls.isSupported()) {
            const hls = new Hls({
                debug: false,
                progressive: true,
                forceKeyFrameOnDiscontinuity: false,
                testBandwidth: false,
                autoStartLoad: true,
            });
            hlsRef.current = hls;
            hls.on(Hls.Events.MANIFEST_PARSED, () => {
                let startLevelIdx = hls.levels.length - 1;
                let minDiff = Infinity;
                for (let i = 0; i < hls.levels.length; i++) {
                    const diff = Math.abs(hls.levels[i].height - 1080);
                    if (diff < minDiff) {
                        minDiff = diff;
                        startLevelIdx = i;
                    }
                }
                hls.startLevel = startLevelIdx;
            });
            hls.loadSource(playlist);
            hls.attachMedia(video);
        } else if (video.canPlayType && video.canPlayType("application/vnd.apple.mpegurl")) {
            video.src = playlist;
        } else {
            video.src = `https://ems.dimu.org/file/${videoUrl}`;
        }

        return () => {
            if (hlsRef.current) {
                hlsRef.current.destroy();
                hlsRef.current = null;
            }
        };
    }, [videoUrl]);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        if (active && !isPaused) {
            const promise = video.play();
            if (promise !== undefined) {
                playPromiseRef.current = promise;
                promise
                    .then(() => {
                        playPromiseRef.current = null;
                    })
                    .catch(() => {
                        playPromiseRef.current = null;
                    });
            }
        } else {
            if (playPromiseRef.current) {
                playPromiseRef.current
                    .then(() => {
                        video.pause();
                    })
                    .catch(() => {});
            } else {
                video.pause();
            }
        }
    }, [active, isPaused]);

    const togglePause = () => {
        setIsPaused(prev => {
            const next = !prev;
            const video = videoRef.current;
            if (video) {
                if (next) {
                    video.pause();
                } else if (active) {
                    video.play().catch(() => {});
                }
            }
            return next;
        });
    };

    if (!videoUrl) {
        return <div className={classes.videoGradient} />;
    }

    return (
        <>
            <div className={classes.videoGradient} />
            <video
                ref={videoRef}
                className={`${classes.video} ${className}`}
                poster={`https://ems.dimu.org/image/${videoUrl}`}
                autoPlay={!isPaused}
                muted
                loop
                playsInline
                preload="auto"
            />
            <button
                type="button"
                className={classes.bgVideoToggle}
                onClick={togglePause}
                aria-label={isPaused ? "Spill av bakgrunnsvideo" : "Pause bakgrunnsvideo"}
            >
                {isPaused ? <Play size={20} /> : <Pause size={20} />}
            </button>
        </>
    );
};
